import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useProviderLoader } from '@/Providers/useProviderLoader'
import { ContainerPage } from '@/components/common'
import { InputControl } from '@/components/form-controls'
import { Button, Card, CardBody, CardHeader, Image } from '@/components/nextui'
import { useAuthApi } from '@/services/api/authApi'
import { ErrorHandlingStrategy, MessageLevel } from '@/types/api'
import { inputValidations } from '@/utils/inputValidations'

export const ResendConfirmation = (): JSX.Element => {
    const { t } = useTranslation()
    const { control, handleSubmit } = useForm<{ email: string }>()
    const { resendConfirmation } = useAuthApi({
        errorHandling: ErrorHandlingStrategy.RETURN,
        messageLevel: MessageLevel.ALL
    })
    const { showLoader, hideLoader } = useProviderLoader()
    const COOLDOWN_SECONDS = 60
    const [isResending, setIsResending] = useState(false)
    const [resendError, setResendError] = useState<string | null>(null)
    const [resendSuccess, setResendSuccess] = useState(false)
    const [cooldownSeconds, setCooldownSeconds] = useState(0)

    useEffect(() => {
        if (cooldownSeconds <= 0) {
            return
        }
        const id = setInterval(() => {
            setCooldownSeconds((s) => (s <= 1 ? 0 : s - 1))
        }, 1000)
        return () => clearInterval(id)
    }, [cooldownSeconds])

    const onSubmit: SubmitHandler<{ email: string }> = async (data) => {
        if (!data.email?.trim() || isResending || cooldownSeconds > 0) {
            return
        }
        setResendError(null)
        setResendSuccess(false)
        setIsResending(true)
        showLoader()
        try {
            const response = await resendConfirmation(data.email.trim())
            if (response.status === 200) {
                setResendError(null)
                setResendSuccess(true)
                setCooldownSeconds(COOLDOWN_SECONDS)
            } else {
                setResendError(
                    response.message?.[0]?.messageDesc ||
                        t('emailSent.resendConfirmationError')
                )
            }
        } catch (error) {
            setResendError(t('emailSent.resendConfirmationError'))
        } finally {
            setIsResending(false)
            hideLoader()
        }
    }

    return (
        <ContainerPage>
            <article className="p-4 bg-gradient-ean-2 dark:bg-gradient-ean">
                <motion.div
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    className="flex flex-col justify-center items-center"
                >
                    <Card className="w-full backdrop-blur-md lg:w-4/5 lg:mx-auto lg:my-0 bg-white/90 dark:bg-gray-800/90">
                        <CardHeader className="flex flex-col gap-4 justify-center items-center">
                            <Image
                                src="/LogoEAN.jpg"
                                alt="logo"
                                className="w-[150px]"
                            />
                            <h1 className="text-2xl font-bold text-center text-primary">
                                {t('emailSent.resendConfirmationTitle')}
                            </h1>
                            <p className="text-center text-default-600 text-sm">
                                {t('emailSent.resendConfirmationHint')}
                            </p>
                        </CardHeader>
                        <CardBody className="flex flex-col gap-4">
                            <form
                                onSubmit={handleSubmit(onSubmit)}
                                className="space-y-4 lg:mx-auto lg:my-0 lg:w-4/5"
                            >
                                <InputControl
                                    allowedPattern={
                                        inputValidations.emailPattern
                                    }
                                    preserveCase
                                    name="email"
                                    control={control}
                                    label={t('forms.common.email')}
                                    rules={{
                                        required: t(
                                            'forms.common.required.emailRequired'
                                        ),
                                        pattern: {
                                            value: inputValidations.isValidEmail,
                                            message: t(
                                                'createAccount.invalidEmail'
                                            )
                                        }
                                    }}
                                    className="text-gray-800"
                                />
                                {resendSuccess && (
                                    <p className="text-center text-sm text-success">
                                        {t('emailSent.resendConfirmationSuccess')}
                                    </p>
                                )}
                                {resendError && (
                                    <p className="text-center text-sm text-danger">
                                        {resendError}
                                    </p>
                                )}
                                <Button
                                    type="submit"
                                    color="primary"
                                    className="w-full"
                                    isDisabled={isResending || cooldownSeconds > 0}
                                >
                                    {cooldownSeconds > 0
                                        ? t('emailSent.resendCooldown', {
                                              seconds: cooldownSeconds
                                          })
                                        : t('emailSent.resendConfirmation')}
                                </Button>
                            </form>
                            <Link
                                to="/login"
                                className="text-xs font-light text-center underline text-disabled"
                            >
                                {t('passwordReset.backToLogin')}
                            </Link>
                        </CardBody>
                    </Card>
                </motion.div>
            </article>
        </ContainerPage>
    )
}
